import { Check, X, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/hooks/useLanguage";

const PricingTable = () => {
  const { t } = useLanguage();
  const f = (key: string) => t(`pricing.table.features.${key}`);
  const ft = (key: string) => { try { return t(`pricing.table.features.${key}Tooltip`); } catch { return undefined; } };
  const s = (key: string) => t(`pricing.table.sections.${key}`);
  const ind = t("pricing.table.features.individual");

  const renderValue = (value: boolean | string) => {
    if (typeof value === "string") return <span className="text-sm text-foreground">{value}</span>;
    return value ? <Check className="w-5 h-5 text-chathero-mint mx-auto" /> : <X className="w-5 h-5 text-destructive/60 mx-auto" />;
  };

  const FeatureRow = ({ fKey, essential, professional, enterprise, tooltipKey }: { fKey: string; essential: boolean | string; professional: boolean | string; enterprise: boolean | string; tooltipKey?: string }) => (
    <tr className="border-b border-border hover:bg-secondary/30 transition-colors">
      <td className="py-4 px-4 text-sm text-foreground"><div className="flex items-center gap-2">{f(fKey)}{tooltipKey && <Tooltip><TooltipTrigger><HelpCircle className="w-4 h-4 text-muted-foreground" /></TooltipTrigger><TooltipContent><p className="max-w-xs">{ft(fKey)}</p></TooltipContent></Tooltip>}</div></td>
      <td className="py-4 px-4 text-center">{renderValue(essential)}</td>
      <td className="py-4 px-4 text-center bg-chathero-mint/5">{renderValue(professional)}</td>
      <td className="py-4 px-4 text-center">{renderValue(enterprise)}</td>
    </tr>
  );

  const SectionHeader = ({ sKey }: { sKey: string }) => (<tr className="bg-secondary/50"><td colSpan={4} className="py-3 px-4 text-sm font-semibold text-primary uppercase tracking-wider">{s(sKey)}</td></tr>);
  const SubHeader = ({ sKey }: { sKey: string }) => (<tr className="border-b border-border/50"><td colSpan={4} className="py-2 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-secondary/30">{s(sKey)}</td></tr>);

  return (
    <div className="overflow-x-auto mb-16">
      <table className="w-full border-collapse bg-background rounded-2xl border border-border overflow-hidden">
        <thead><tr className="border-b border-border">
          <th className="py-4 px-4 text-left w-1/3"></th>
          <th className="py-4 px-4 text-center w-[22%]"><span className="text-lg font-bold text-primary">Essential</span></th>
          <th className="py-4 px-4 text-center w-[22%] bg-chathero-mint/5"><div className="flex flex-col items-center gap-1"><span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-0.5 rounded-full">{t("pricing.table.popular")}</span><span className="text-lg font-bold text-primary">Professional</span></div></th>
          <th className="py-4 px-4 text-center w-[22%]"><span className="text-lg font-bold text-primary">Enterprise</span></th>
        </tr></thead>
        <tbody>
          <SectionHeader sKey="usage" />
          <FeatureRow fKey="knowledgeBase" essential="2M" professional="5M" enterprise={ind} tooltipKey="knowledgeBase" />
          <FeatureRow fKey="messages" essential="1.000" professional="5.000" enterprise={ind} tooltipKey="messages" />
          <FeatureRow fKey="assistants" essential="1" professional="2" enterprise={ind} />
          <FeatureRow fKey="teamUser" essential="bis zu 2" professional="bis zu 5" enterprise={ind} />
          <FeatureRow fKey="llm" essential={true} professional={true} enterprise={true} />
          <SectionHeader sKey="core" />
          <SubHeader sKey="chatMessaging" />
          <FeatureRow fKey="inbox" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="webchat" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="whatsapp" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="whatsappBroadcast" essential={false} professional={true} enterprise={true} />
          <FeatureRow fKey="facebook" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="liveChat" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="analytics" essential={true} professional={true} enterprise={true} />
          <SubHeader sKey="connectivity" />
          <FeatureRow fKey="crm" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="integrations" essential={false} professional={true} enterprise={true} />
          <SectionHeader sKey="onboarding" />
          <FeatureRow fKey="onboardingServices" essential={false} professional={true} enterprise={true} />
          <FeatureRow fKey="support" essential={false} professional={true} enterprise={true} />
          <SectionHeader sKey="additional" />
          <FeatureRow fKey="leadManagement" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="automationFlow" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="brandTool" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="editableFields" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="botPersona" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="embedFormats" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="rbac" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="feedback" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="emailNotifications" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="websiteIntegration" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="scheduling" essential={true} professional={true} enterprise={true} />
          <FeatureRow fKey="whiteLabel" essential={false} professional={false} enterprise={true} />
        </tbody>
      </table>
    </div>
  );
};
export default PricingTable;
